/**
 * ProjectDropdown — master project selector sitting above the sidebar tabs.
 * Lists all projects, lets you switch active project, create, edit, delete.
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { useUiStore } from '../../stores/uiStore.js';
import { LoadingSpinner } from '../ui/index.js';
import type { Project } from '../../types/index.js';

export function ProjectDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { activeProject, setActiveProject } = useMapStore();
  const { openModal } = useUiStore();

  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const result = await api.get<{ projects: Project[] }>('/projects?limit=100');
      return result.success ? result.data.projects : [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      if (activeProject?.id === id) setActiveProject(null);
      toast.success('Project deleted');
    },
  });

  // Close on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const projects = data ?? [];

  const select = (p: Project) => {
    setActiveProject(activeProject?.id === p.id ? null : p);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative border-b border-gray-100">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 shrink-0">Project</span>
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : activeProject ? (
            <span className="text-sm font-medium text-gray-900 truncate">{activeProject.name}</span>
          ) : (
            <span className="text-sm text-gray-400 italic">Select a project…</span>
          )}
        </div>
        <span className={`text-gray-400 text-xs transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 shadow-xl rounded-b-xl overflow-hidden">
          {projects.length === 0 && !isLoading ? (
            <p className="text-xs text-gray-400 text-center py-4">No projects yet</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-50">
              {projects.map(p => (
                <li
                  key={p.id}
                  className={`flex items-center gap-2 px-4 py-2.5 cursor-pointer transition-colors ${activeProject?.id === p.id ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                  onClick={() => select(p)}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${activeProject?.id === p.id ? 'text-brand-700' : 'text-gray-800'}`}>{p.name}</p>
                    {p.description && <p className="text-xs text-gray-400 truncate">{p.description}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => { openModal('projectForm', p); setOpen(false); }}
                      className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 text-xs"
                      title="Edit"
                    >✎</button>
                    <button
                      onClick={() => { if (confirm('Delete this project and all its data?')) deleteMutation.mutate(p.id); }}
                      className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 text-xs"
                      title="Delete"
                    >✕</button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-gray-100 px-4 py-2">
            <button
              onClick={() => { openModal('projectForm'); setOpen(false); }}
              className="w-full text-left text-sm text-brand-600 hover:text-brand-700 font-medium py-1"
            >
              + New Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
